/**
 * Last vs previous close → a market-snapshot item (ADR-0032).
 *
 * A missing last bar omits the instrument. A missing previous bar still
 * emits last with null change — never 0 (R2b).
 */
import {
  SNAPSHOT_INSTRUMENTS,
  snapshotInstrument,
  type MarketSnapshotItem,
  type SnapshotCode,
  type SnapshotInstrument,
} from '@meridian/contracts';

export interface ClosePoint {
  readonly on: string;
  readonly close: number;
}

export function snapshotItemFromCloses(
  instrument: SnapshotInstrument,
  last: ClosePoint,
  prev: ClosePoint | null,
): MarketSnapshotItem | null {
  if (!(last.close > 0) || !Number.isFinite(last.close)) return null;
  if (prev === null || !(prev.close > 0) || !Number.isFinite(prev.close)) {
    return {
      code: instrument.code,
      name: instrument.name,
      category: instrument.category,
      last: last.close,
      change: null,
      changePct: null,
      asOn: last.on,
    };
  }
  const change = last.close - prev.close;
  return {
    code: instrument.code,
    name: instrument.name,
    category: instrument.category,
    last: last.close,
    change,
    changePct: change / prev.close,
    asOn: last.on,
  };
}

/** `bars` oldest-first. Empty / non-positive last → omit. */
export function itemFromBars(
  code: SnapshotCode,
  bars: readonly ClosePoint[],
): MarketSnapshotItem | null {
  const last = bars[bars.length - 1];
  if (last === undefined) return null;
  const prev = bars.length >= 2 ? (bars[bars.length - 2] ?? null) : null;
  return snapshotItemFromCloses(snapshotInstrument(code), last, prev);
}

/** Catalog order, skipping codes with no usable last bar. */
export function buildMarketSnapshotItems(
  barsByCode: ReadonlyMap<SnapshotCode, readonly ClosePoint[]>,
): MarketSnapshotItem[] {
  return SNAPSHOT_INSTRUMENTS.flatMap((instrument) => {
    const item = itemFromBars(instrument.code, barsByCode.get(instrument.code) ?? []);
    return item === null ? [] : [item];
  });
}
